import { BaseQueryApi, fetchBaseQuery } from '@reduxjs/toolkit/query';
import type { FetchArgs } from '@reduxjs/toolkit/query';
import {
  FetchBaseQueryArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/dist/query/fetchBaseQuery';
import config from '@/lib/config';
import { BaseQueryFn } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { Mutex, MutexInterface } from 'async-mutex';

const OAUTH2_CLIENT_PATH_PREFIX = '/oauth2-client';
const REDIRECT_PATH_PREFIX = '/redirect-to-secure-frontend/null?frontend-url=';

export const ANONYMOUS_ROUTE_PREFIXES = ['/breathe/'];

export function isAnonymousRoute(pathname: string): boolean {
  return ANONYMOUS_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function getRedirectUrl(): string {
  return `${
    config.api.baseUrl
  }${OAUTH2_CLIENT_PATH_PREFIX}${REDIRECT_PATH_PREFIX}${encodeURIComponent(
    window.location.href,
  )}`;
}

const mutex = new Mutex();
const fetchBaseQueryWrapper = (
  baseQueryOptions: FetchBaseQueryArgs | undefined,
) => {
  const baseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    // eslint-disable-next-line @typescript-eslint/ban-types
    {},
    FetchBaseQueryMeta
  > = fetchBaseQuery(baseQueryOptions);

  return async (
    args: string | FetchArgs,
    api: BaseQueryApi,
    // eslint-disable-next-line @typescript-eslint/ban-types
    extraOptions: {},
  ) => {
    // wait until the mutex is available without locking it
    await mutex.waitForUnlock();
    // Perform any pre-processing here if needed
    // Call the actual baseQuery
    const baseQueryResponse = await baseQuery(args, api, extraOptions);
    // Perform any post-processing here if needed
    if (
      baseQueryResponse.error &&
      !isAnonymousRoute(window.location.pathname) &&
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      (baseQueryResponse.error.originalStatus === 401 ||
        baseQueryResponse.error.status === 401 ||
        baseQueryResponse.meta?.response?.status === 401)
    ) {
      if (!mutex.isLocked()) {
        // lock the mutex
        const release: MutexInterface.Releaser = await mutex.acquire();
        // redirect to the login page for user authentication
        try {
          window.location.href = getRedirectUrl();
        } finally {
          release();
        }
      }
    }
    return baseQueryResponse;
  };
};

export default fetchBaseQueryWrapper;
